import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePortalUsers } from '@/hooks/usePortalUsers';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface NewInspectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  tenantId: string | null;
  onCreated: (inspection: any) => void;
}

const INSPECTION_TYPES = [
  'Veiligheid',
  'Kwaliteit',
  'Onderhoud',
  'Installatie',
  'Oplevering',
  'Periodieke controle',
  'Anders'
];

export function NewInspectionModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  tenantId,
  onCreated
}: NewInspectionModalProps) {
  const [formData, setFormData] = useState({
    inspector_id: '',
    type: '',
    date: new Date(),
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: inspectors, isLoading: loadingInspectors } = usePortalUsers('inspector');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.inspector_id || !formData.type) return;

    setIsSubmitting(true);
    try {
      const inspection = {
        project_id: projectId,
        tenant_id: tenantId,
        inspector_id: formData.inspector_id,
        type: formData.type,
        date: format(formData.date, 'yyyy-MM-dd'),
        notes: formData.notes,
        status: 'concept',
        photo_urls: []
      };

      onCreated(inspection);
      handleClose();
    } catch (error) {
      console.error('Error creating inspection:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      inspector_id: '',
      type: '',
      date: new Date(),
      notes: ''
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nieuwe Inspectie</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Project</Label>
            <Input value={projectName} disabled className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="inspector">Inspecteur *</Label>
            {loadingInspectors ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Laden...
              </div>
            ) : (
              <Select
                value={formData.inspector_id}
                onValueChange={(value) => setFormData({ ...formData, inspector_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer inspecteur" />
                </SelectTrigger>
                <SelectContent>
                  {inspectors?.map((inspector) => (
                    <SelectItem key={inspector.id} value={inspector.id}>
                      {inspector.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Type *</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer type" />
              </SelectTrigger>
              <SelectContent>
                {INSPECTION_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Datum *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.date ? format(formData.date, 'PPP', { locale: nl }) : 'Selecteer datum'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.date}
                  onSelect={(date) => date && setFormData({ ...formData, date })}
                  locale={nl}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notities</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Eventuele opmerkingen..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={isSubmitting || !formData.inspector_id || !formData.type}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aanmaken...
                </>
              ) : (
                'Inspectie Aanmaken'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
